import { useCallback, useEffect, useState } from 'react'

import { PageTitle } from '@/components'
import { Button, Checkbox, Modal } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import {
  assignVendorLocationProduct,
  assignVendorLocationUser,
  getVendorProducts,
  getVendorUsers,
  IVendorProduct,
  IVendorUser,
} from '@/core/services/vendors'

interface AssignModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendorId: string
  locationId: string
  type: 'user' | 'product'
  assignedIds: string[]
  onSuccess: () => void
  onError: (message: string) => void
}

export const AssignModal = ({
  open,
  onOpenChange,
  vendorId,
  locationId,
  type,
  assignedIds,
  onSuccess,
  onError,
}: AssignModalProps) => {
  const [items, setItems] = useState<(IVendorUser | IVendorProduct)[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const isUser = type === 'user'
  const title = isUser ? 'User' : 'Product'

  const fetchItems = useCallback(async () => {
    try {
      if (isUser) {
        const { data } = await getVendorUsers(vendorId)
        setItems(data.filter((u) => !assignedIds.includes(u.id)))
      } else {
        const { data } = await getVendorProducts(vendorId)
        setItems(data.filter((p) => !assignedIds.includes(p.id)))
      }
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    }
  }, [vendorId, type, assignedIds])

  useEffect(() => {
    if (!open) return
    fetchItems()
    return () => {
      setSelected([])
      setItems([])
    }
  }, [open, fetchItems])

  const handleToggle = (id: string, checked: boolean) => {
    setSelected(checked ? [...selected, id] : selected.filter((i) => i !== id))
  }

  const handleAssign = async () => {
    if (!selected.length) return
    setLoading(true)
    try {
      if (isUser) {
        await assignVendorLocationUser(vendorId, locationId, {
          ids: selected,
        })
      } else {
        await assignVendorLocationProduct(vendorId, locationId, {
          ids: selected,
        })
      }
      onSuccess()
      onOpenChange(false)
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const columns: IColumn[] = [
    {
      title: '',
      accessor: '',
      render: (row: IVendorUser | IVendorProduct) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleToggle(row.id, checked)}
          checked={selected.includes(row.id)}
        />
      ),
    },
    ...(isUser
      ? [
          {
            title: 'Name',
            accessor: 'name',
            sortable: true,
            searchable: true,
          },
          {
            title: 'Phone',
            accessor: 'phone',
            sortable: true,
          },
        ]
      : [
          {
            title: 'Image',
            accessor: 'photo',
            render: (row: IVendorProduct) =>
              row.imageLink ? (
                <img
                  src={row.imageLink}
                  alt={row.name}
                  className="h-10 w-10 rounded-md object-cover"
                />
              ) : (
                <div className="h-10 w-10 rounded-md bg-monochrome-3-60" />
              ),
          },
          {
            title: 'Name',
            accessor: 'name',
            sortable: true,
            searchable: true,
          },
          {
            title: 'Price',
            accessor: 'price',
            sortable: true,
            render: (row: IVendorProduct) => <span>${row.price}</span>,
          },
        ]),
  ]

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="xl"
      title={
        <div className="border-b border-monochrome-120 pb-4">
          <PageTitle
            icon="Vendor Entity"
            title={
              <span className="flex items-center gap-2">
                Assign <span className="text-accent-1">{title}</span>
              </span>
            }
          />
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button
            onClick={() => onOpenChange(false)}
            type="button"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selected.length || loading}
            variant="primary"
          >
            Assign {title}
            {selected.length > 0 && ` (${selected.length})`}
          </Button>
        </div>
      }
    >
      {items.length ? (
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={items}
        />
      ) : (
        <div className="flex items-center justify-center py-10 text-monochrome-2">
          No {isUser ? 'users' : 'products'} available to assign
        </div>
      )}
    </Modal>
  )
}
